import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ClientInitializerService, DebugService, IPaytrailData, IPaytrailResponseDto, LanguageService, LoaderService, PIMService, ReservationService, SettingsService, ShopcartService } from 'core-lib';
import { Router } from '@angular/router';
import { IShopCart, ICartItem, IProfileData } from 'core-lib';
import { MessageModule } from 'primeng/message';
import { mandatoryDepositType } from 'core-lib';
import { CheckboxModule } from 'primeng/checkbox';
import { DrawerModule } from 'primeng/drawer';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { PaytrailPaymentFormComponent } from 'core-lib';
import { catchError, Subscription } from 'rxjs';

@Component({
    selector: 'app-profile-payment',
    imports: [ButtonModule, TranslateModule, MessageModule, CheckboxModule, DrawerModule, TooltipModule, ReactiveFormsModule, PaytrailPaymentFormComponent],
    templateUrl: './profile-payment.component.html',
    styleUrl: './profile-payment.component.scss'
})
export class ProfilePaymentComponent implements OnInit, OnDestroy {

  @Input() shopCart:IShopCart;
  @Input() validationData:any;
  @Input() ticketExtraId:undefined|string; // if ticketExtraId, payment needs to be forced

  paymentServiceProvider: 'paytrail' | 'viva' | null = null;

  allRoomsValidated:boolean = false;
  selectedPaymentMethod:string|null = null;
  cancellationPolicies:any[];
  roomCount:number;
  reservationErrorMessage: null|string = null;
  availablePaymentMethods:string[] = [];
  forcePrepaid:boolean = false;
  acceptTerms:FormControl = new FormControl(false, Validators.requiredTrue); 
  termsAndConditionsOpened:boolean = false;
  termsAndConditionsModalContent:any = "";
  paymentFormData:null|IPaytrailData = null; //list of payment options when payment is initiated. Map selected payment option to this to get form that needs to be posted to paytrail
  paymentFormCreated:boolean = false;
  paymentOptions:IPaytrailData; // list of possible paymentOptions provided by paytrail //nordea, op, visa etc.
  selectedPaymentOption:null|string=null
  selectedPaymentButton:Record<string,string>|null =null;
  shopCartHasExternalExtras:boolean = false;
  paymentServiceState: 'loading' | 'available' | 'none' | 'unavailable' = 'loading';
  private paymentServiceSubscription: Subscription | null = null;

  // the booking must be prepaid, but the hotel has no payment service provider or it could not be loaded
  get paymentServiceUnavailable():boolean {
    return this.forcePrepaid && (this.paymentServiceState == 'none' || this.paymentServiceState == 'unavailable');
  }

  constructor(
    private reservationService: ReservationService, 
    private translateService: TranslateService,
    public settings: SettingsService,
    private languageService: LanguageService,
    private clientInitializerService: ClientInitializerService,
    private shopCartService: ShopcartService, 
    private debug: DebugService,
    private pimService: PIMService
  ) {}

  ngOnInit(): void {

    this.forcePrepaid = false; //reset force prepaid logic because ngOnInit is called in changes also
    this.availablePaymentMethods = []; //reset available paymentmethods, because onInit could be called again
    if (this.shopCart.hasExternalExtras()) {
      this.shopCartHasExternalExtras = true;
      this.forcePrepaid = true;
    }
    this.getAvailablePaymentMethods();
    this.getAllCancellationPolicies();

    // first get payment service provider. ngOnInit is called again on changes, so an earlier lookup is dropped and its
    // late answer cannot overwrite the state of this one
    this.paymentServiceProvider = null;
    this.paymentServiceState = 'loading';
    this.paymentServiceSubscription?.unsubscribe();
    this.paymentServiceSubscription = this.reservationService.getPaymentServiceProvider().subscribe({
      next: (res:any) => {
        // psp is null only when the hotel has no payment service provider configured (or the tenant has no payment
        // endpoint at all)
        this.paymentServiceProvider = res?.psp?.toLowerCase() ?? null;
        this.paymentServiceState = this.paymentServiceProvider ? 'available' : 'none';
        this.resolvePrepaidPayment();

        if (this.paymentServiceProvider == 'paytrail') {

          //then get payment options
          if (this.availablePaymentMethods.includes('cc') || this.forcePrepaid) {
            this.reservationService.getPaytrailPaymentOptions(this.shopCart.getTotalPrice()).subscribe((res:any) => {
              if (res.success) {
                this.paymentOptions = res.data;
              }
            });
          }
        }
      },
      // a failed lookup (e.g. 503 or a network error) does not tell whether the hotel takes payments
      error: () => {
        this.paymentServiceProvider = null;
        this.paymentServiceState = 'unavailable';
        this.resolvePrepaidPayment();
      }
    });

    

    this.roomCount = this.shopCart.getCartItemsByType('roomrate')?.length;

    if (this.settings.get('profile.termsAndConditionsAsModal')) {
      let hotelCode = this.shopCart.getFirstSearchParams()?.hotelCode;
      let tcFromPIM = this.pimService.getTermsAndConditionsHtml(hotelCode);
      if (tcFromPIM && tcFromPIM?.trim() !== "") {
        this.termsAndConditionsModalContent = tcFromPIM;
      }
      else {
        this.clientInitializerService.getTermsAndConditionsHtml().subscribe((tc) => {
        if (tc) {
          this.termsAndConditionsModalContent = tc;
        }
      }); 
      }
    }
  }

  ngOnDestroy(): void {
    this.paymentServiceSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    let isValid = true;
    this.validationData.forEach((vs:IProfileData, index:number) => {
      if (vs?.isValid == false) {
        isValid = false;
      }
    });
    this.allRoomsValidated = isValid;

    if (changes.shopCart) {
      if (this.shopCart.hasExternalExtras()) {
        this.shopCartHasExternalExtras = true;
        this.debug.log('PAYMENT: forcing prepaid, because has external extras');
      }
      else {
        this.shopCartHasExternalExtras = false;
      }
     
      this.ngOnInit();
    }
    
  }


  selectPaymentMethod(method:string) {
    this.selectedPaymentMethod = method;
  }

  preparePhoneNumberForReservation() {
  this.validationData.forEach((item: IProfileData) => {

    let phoneFull = item.profile._phone_full;
    let phoneLocal = item.profile.phoneNumber;

    // If phone_full exists and is Finnish
    if (phoneFull && phoneFull.startsWith('+358')) {
      // Get part after +358
      let localPart = phoneFull.slice(4);

      // If local part starts with 04 or 05 → remove first zero
      if (localPart.startsWith('04') || localPart.startsWith('05')) {
        localPart = localPart.slice(1);
      }

      // Update both full and local
      phoneFull = '+358' + localPart;
      phoneLocal = localPart;
    }

    // Save changes
    item.profile._phone_full = phoneFull;
    item.profile.phoneNumber = phoneLocal;

  });
  }

  makeReservation() {
    this.hideReservationErrorMessage();
    this.preparePhoneNumberForReservation();
    let profiles = this.validationData.map((item:IProfileData) => item.profile);
    this.shopCartService.addProfiles(profiles);
    
    //let profile = this.validationData.profile;
    this.reservationService.makeReservationWithoutPayment().subscribe({
      error: () => {
        this.showReservationErrorMessage()
      }
    });
  }

  makePayment() {
    this.hideReservationErrorMessage();
    this.preparePhoneNumberForReservation();

    if (this.selectedPaymentOption == null && this.paymentServiceProvider == 'paytrail') {
      this.showReservationErrorMessage(this.translateService.instant('pr.selectPaymentOptionError'));
      return;
    }

    let profiles = this.validationData.map((item:IProfileData) => item.profile);
    this.shopCartService.addProfiles(profiles);

    if (this.paymentServiceProvider == 'paytrail' && this.selectedPaymentOption) {
      this.reservationService.startPaytrailPaymentProcess(this.selectedPaymentOption).subscribe({
        
        // the reservation service makes a reservation, and returns a paymentform based on our selected payment option. When form is submitted the user is directed to the payment window.
        next: (formResponse:IPaytrailResponseDto) => {

          if (formResponse instanceof HTMLFormElement) {
            // Append the form to the document body so it becomes part of the DOM
            document.body.appendChild(formResponse);
            // Submit the form to perform the POST request
            formResponse.submit();
          }
          else {
            this.showReservationErrorMessage()
          }
        },
        error: () => {
          this.showReservationErrorMessage()
        }
      });
    }
    else if (this.paymentServiceProvider == 'viva') {
      this.reservationService.startVivaPaymentProcess().subscribe({
        // the reservation service makes a reservation, and returns a paymentform based on our selected payment option. When form is submitted the user is directed to the payment window.
        next: (formResponse:IPaytrailResponseDto) => {

          if (formResponse instanceof HTMLFormElement) {
            // Append the form to the document body so it becomes part of the DOM
            document.body.appendChild(formResponse);
            // Submit the form to perform the POST request
            formResponse.submit();
          }
          else {
            this.showReservationErrorMessage()
          }
        },
        error: () => {
          this.showReservationErrorMessage()
        }
      });
    }
  }

  showReservationErrorMessage(message?:string) {
    if (message) {
      this.reservationErrorMessage = message;
    }
    else {
      this.reservationErrorMessage = this.translateService.instant('pr.ReservationError');
    }
  }

  hideReservationErrorMessage() {
    this.reservationErrorMessage = null;
  }

  scrollToBookerDetails(roomIndex:number) {

    document.querySelector('.profile-booker-details-header[roomIndex="'+roomIndex+'"]')?.scrollIntoView();
  }

  getAvailablePaymentMethods() {

    // the payment method comes from the rate: a rate with a deposit is always paid in advance, because the server
    // creates such a reservation only as temporary and confirms it after the payment
    this.shopCart.getCartItemsByType('roomrate').forEach((cartItem:ICartItem) => {
      if (cartItem.item.rate.mandatoryDepositType == mandatoryDepositType.PAYMENT_AT_ARRIVAL) {
        if (!this.availablePaymentMethods.includes('hold')) {
          this.availablePaymentMethods.push('hold');
        }

      }
      else {
        if (!this.availablePaymentMethods.includes('cc')) {
          this.availablePaymentMethods.push('cc');
          this.forcePrepaid = true;
        }

      }
    });

    if (this.availablePaymentMethods.length == 0) {
      console.error('NO PAYMENT METHODS SET');
    }
    
    if (this.availablePaymentMethods.length == 1) {
      // preselect the payment method
      this.selectedPaymentMethod = this.availablePaymentMethods[0];
    }
    if (this.forcePrepaid) {
      this.selectedPaymentMethod = 'cc';
    }
    
  }

  resolvePrepaidPayment() {

    // a hotel without a payment service provider cannot take payments, so external extras (tickets) on rates paid at
    // arrival are not forced to be prepaid: the server books them without a deposit. Only a verified missing
    // configuration allows this, never a failed lookup.
    if (this.paymentServiceState == 'none' && this.shopCartHasExternalExtras && !this.availablePaymentMethods.includes('cc')) {
      this.debug.log('PAYMENT: not forcing prepaid for external extras, because the hotel has no payment service provider');
      this.forcePrepaid = false;
      if (this.availablePaymentMethods.length == 1) {
        this.selectedPaymentMethod = this.availablePaymentMethods[0];
      }
    }

    // a booking that must be prepaid cannot be made without a payment service provider
    if (this.paymentServiceUnavailable) {
      console.error('PAYMENT: the booking must be prepaid, but no payment service provider is available');
    }
  }

  getAllCancellationPolicies() {

    /* gets all cancellation policies, and sets same policy under ame object eg
    * {
    *     roomNumber: [1,3],
    *     policy: "foo faa foo"
    *   }
    */

    let cp:any[] = [];
    this.shopCart.getCartItemsByType('roomrate').forEach((cartItem:ICartItem, index) => {
      if (cartItem.item.rate.pim?.cancellationPolicy) {
        
        let found = cp.findIndex((p:any) => p.policy == cartItem.item.rate.pim.cancellationPolicy);
        if (found == -1) {
          //not found
          let policyObj = {
            roomNumber: [index + 1],
            policy: cartItem.item.rate.pim.cancellationPolicy
          }
          cp.push(policyObj);
        } 
        else {
          cp[found].roomNumber.push(index+1);
        }

        
      }
    });
    this.cancellationPolicies = cp;
  }

  setSelectedPaymentOption(option:string) {
    this.selectedPaymentOption = option;
    this.selectedPaymentButton = {
      logo: option.split('|')[3],
      text: option.split('|')[2]
    };
    this.hideReservationErrorMessage();

  }

  clearPaymentOption() {
    this.selectedPaymentOption = null;
  }

  openTermsAndConditions() {
    if (this.settings.get('profile.termsAndConditionsAsModal')) {
      this.termsAndConditionsOpened = true;
    }
    else {
      let urls = this.settings.get('profile.termsAndConditionsUrls')
      let language = this.languageService.getCurrentLanguage();
      let urlObj = urls.find((item:any) => item.lang == language);
      if (urlObj) {
        window.open(urlObj.url, '_blank');
      }
    }
  }

  acceptTermsInModal() {
    this.termsAndConditionsOpened = false
    this.acceptTerms.setValue(true);
  }
}
